/**
 * Bundle 80.1 Task 5 - Connection Funnel tile.
 *
 * Rendered at the top of /admin/analytics for super_admins. Shows the
 * full Bundle 76-80 pipeline (registered -> checked in -> survey -> intro
 * requested -> call interested -> mutual -> 14d positive -> success
 * candidate -> published) as horizontal bars with counts + conv %.
 * Event selector across all events / specific event / date range.
 * Click a stage to drill in (drill state stays local; hard drills are
 * planned as a follow-up).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '@/utils/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { RefreshCw, X, Download } from 'lucide-react';

const STAGE_COLORS = [
  'bg-primary-800', 'bg-primary-700', 'bg-primary-600', 'bg-secondary-600',
  'bg-secondary-500', 'bg-emerald-700', 'bg-emerald-600', 'bg-emerald-500', 'bg-emerald-400',
];

const ConnectionFunnelTile = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState('all');
  const [days, setDays] = useState(90);
  const [error, setError] = useState('');
  const [drillStage, setDrillStage] = useState(null);

  const load = useCallback(async (refresh = 0) => {
    setLoading(true);
    setError('');
    try {
      const { data: d } = await api.get('/admin/analytics/connection-funnel', {
        params: { event_id: eventId, days, refresh },
      });
      setData(d);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load funnel');
    } finally {
      setLoading(false);
    }
  }, [eventId, days]);

  useEffect(() => { load(0); }, [load]);

  useEffect(() => {
    let m = true;
    (async () => {
      try {
        const { data: idx } = await api.get('/admin/analytics/event-survey-index');
        if (m) setEvents(idx?.events || []);
      } catch (_) {
        // Non-blocking; the tile still works with the "All events" default.
      }
    })();
    return () => { m = false; };
  }, []);

  const top = useMemo(() => data?.stages?.[0]?.count || 0, [data]);

  return (
    <Card className="mb-4" data-testid="connection-funnel-tile">
      <CardContent className="p-5 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-secondary-800">Bundle 80.1 * Connection funnel</div>
            <h3 className="font-display text-lg font-bold text-primary-900">RSVP to published story</h3>
            <p className="text-xs text-neutral-600">
              Full Bundle 76-80 pipeline in one view. Cached 60s.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <select
              value={eventId}
              onChange={(e) => setEventId(e.target.value)}
              className="border border-neutral-200 rounded-md px-2 py-1 text-sm"
              data-testid="funnel-event-selector"
            >
              <option value="all">All events</option>
              {events.map((ev) => (
                <option key={ev.id} value={ev.id}>{ev.title || ev.id}</option>
              ))}
            </select>
            <select
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              className="border border-neutral-200 rounded-md px-2 py-1 text-sm"
              data-testid="funnel-days-selector"
            >
              <option value={30}>Last 30 days</option>
              <option value={60}>Last 60 days</option>
              <option value={90}>Last 90 days</option>
              <option value={180}>Last 180 days</option>
              <option value={365}>Last 365 days</option>
            </select>
            <Button
              size="sm" variant="outline"
              onClick={() => load(1)}
              disabled={loading}
              data-testid="funnel-refresh-btn"
            >
              <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/analytics/connection-funnel.csv?event_id=${encodeURIComponent(eventId)}&days=${days}`}
              className="inline-flex items-center gap-1 border border-neutral-200 rounded-md px-2 py-1 text-sm text-primary-900 hover:bg-neutral-50"
              data-testid="funnel-csv-btn"
              download
            >
              <Download className="h-3.5 w-3.5" /> CSV
            </a>
            {data?.cached ? <Badge variant="outline" data-testid="funnel-cached-badge">cached</Badge> : null}
          </div>
        </div>

        {error ? (
          <div className="text-xs text-rose-700" data-testid="funnel-error">{error}</div>
        ) : null}

        {data?.stages?.length ? (
          <div className="space-y-2">
            {data.stages.map((s, idx) => {
              const width = top ? Math.max(4, Math.round((s.count / top) * 100)) : 4;
              const color = STAGE_COLORS[idx] || 'bg-primary-800';
              return (
                <button
                  key={s.key}
                  type="button"
                  onClick={() => setDrillStage(s.key)}
                  className="w-full text-left group"
                  data-testid={`funnel-stage-${s.key}`}
                >
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="text-primary-900 font-medium">{s.label}</span>
                    <span className="text-neutral-600">
                      <span className="font-mono" data-testid={`funnel-count-${s.key}`}>{s.count}</span>
                      {s.conv_pct_vs_prev != null ? (
                        <span className="ml-2 text-neutral-500">
                          vs prev: <span data-testid={`funnel-conv-prev-${s.key}`}>{s.conv_pct_vs_prev}%</span>
                        </span>
                      ) : null}
                      {s.conv_pct_vs_top != null && idx > 0 ? (
                        <span className="ml-2 text-neutral-500">
                          vs top: <span data-testid={`funnel-conv-top-${s.key}`}>{s.conv_pct_vs_top}%</span>
                        </span>
                      ) : null}
                    </span>
                  </div>
                  <div className="h-3 bg-neutral-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${color} transition-all duration-500 group-hover:opacity-90`}
                      style={{ width: `${width}%` }}
                    />
                  </div>
                  {drillStage === s.key ? (
                    <div className="mt-2 text-[11px] text-neutral-600 pl-2 border-l-2 border-primary-200" data-testid={`funnel-drill-${s.key}`}>
                      Loading drill-down in the side panel...
                    </div>
                  ) : null}
                </button>
              );
            })}
          </div>
        ) : (
          <div className="text-xs text-neutral-500">
            {loading ? 'Loading funnel...' : 'No funnel data.'}
          </div>
        )}

        {data?.generated_at ? (
          <div className="text-[10px] text-neutral-500">
            Generated {new Date(data.generated_at).toLocaleString()}
          </div>
        ) : null}
      </CardContent>
      {drillStage ? (
        <FunnelDrillPanel
          stage={drillStage}
          eventId={eventId}
          days={days}
          onClose={() => setDrillStage(null)}
        />
      ) : null}
    </Card>
  );
};

const FunnelDrillPanel = ({ stage, eventId, days, onClose }) => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');
  const [sortDir, setSortDir] = useState('desc');

  useEffect(() => {
    let m = true;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const { data } = await api.get('/admin/analytics/connection-funnel/drill', {
          params: { stage, event_id: eventId, days, limit: 200 },
        });
        if (m) setRecords(data?.records || []);
      } catch (e) {
        if (m) setError(e?.response?.data?.detail || 'Drill failed');
      } finally {
        if (m) setLoading(false);
      }
    })();
    return () => { m = false; };
  }, [stage, eventId, days]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const rows = needle
      ? records.filter((r) => JSON.stringify(r).toLowerCase().includes(needle))
      : records;
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      const av = String(a.at || '');
      const bv = String(b.at || '');
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [records, q, sortDir]);

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="funnel-drill-panel">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <div className="w-full max-w-2xl bg-white h-full overflow-auto p-5 shadow-2xl">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-secondary-800">Funnel drill-down</div>
            <h3 className="font-display text-lg font-bold text-primary-900">Stage: {stage}</h3>
            <div className="text-xs text-neutral-600">
              Event {eventId} * last {days} days * {filtered.length}{filtered.length === records.length ? '' : ` of ${records.length}`} rows
            </div>
          </div>
          <button
            type="button" onClick={onClose}
            className="p-1 rounded-md border border-neutral-200 hover:bg-neutral-50"
            data-testid="drill-close-btn"
          >
            <X className="h-4 w-4 text-primary-900" />
          </button>
        </div>
        <div className="flex items-center gap-2 mb-3">
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search id, event, status..."
            className="text-sm"
            data-testid="drill-search-input"
          />
          <Button
            size="sm" variant="outline"
            onClick={() => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))}
            data-testid="drill-sort-btn"
          >
            Time {sortDir === 'asc' ? 'asc' : 'desc'}
          </Button>
        </div>
        {error ? <div className="text-rose-700 text-xs mb-2">{error}</div> : null}
        {loading ? (
          <div className="text-neutral-500 text-sm">Loading...</div>
        ) : filtered.length === 0 ? (
          <div className="text-neutral-500 text-sm border border-dashed border-neutral-200 rounded-md p-4 text-center">
            No records in this stage.
          </div>
        ) : (
          <div className="space-y-1" data-testid="drill-rows">
            {filtered.map((r) => (
              <div key={r.id} className="border border-neutral-200 rounded-md p-2 text-xs">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono text-primary-900" data-testid={`drill-row-id-${r.id}`}>{r.id}</span>
                  <span className="text-neutral-500">{r.at ? new Date(r.at).toLocaleString() : ''}</span>
                </div>
                <div className="mt-1 text-primary-800">{r.label}</div>
                {r.event_id ? (
                  <div className="mt-1 text-[10px] text-neutral-500 font-mono">event {r.event_id}</div>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ConnectionFunnelTile;
